import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Icon, { type IconName } from "@/components/Icon";

/** Toasts transitoires (succès / info / erreur), maison (aucune librairie UI).
 *  Empilables, auto-fermés après `duration`, annoncés aux lecteurs d'écran via une
 *  région aria-live="polite". Les timeouts sont nettoyés au démontage.
 *
 *  Usage :  const toast = useToast(); toast.success("Exercice validé"); */
type ToastKind = "success" | "info" | "error";
interface ToastItem { id: number; kind: ToastKind; message: string }
interface ToastApi {
  show: (message: string, kind?: ToastKind, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast doit être utilisé dans <ToastProvider>");
  return api;
}

const ICON: Record<ToastKind, IconName> = { success: "done", info: "status", error: "alert" };
const COLOR: Record<ToastKind, string> = { success: "var(--green)", info: "var(--blue)", error: "var(--red)" };

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<number, number>>(new Map());
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle !== undefined) { window.clearTimeout(handle); timers.current.delete(id); }
  }, []);

  const show = useCallback((message: string, kind: ToastKind = "info", duration = 3000) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, kind, message }]);
    timers.current.set(id, window.setTimeout(() => dismiss(id), duration));
  }, [dismiss]);

  // Nettoyage : annule tous les timeouts en attente au démontage (pas de fuite).
  useEffect(() => {
    const map = timers.current;
    return () => { map.forEach((h) => window.clearTimeout(h)); map.clear(); };
  }, []);

  // Identité stable → utilisable sans risque dans des tableaux de dépendances.
  const api = useMemo<ToastApi>(() => ({
    show,
    success: (m, d) => show(m, "success", d),
    error: (m, d) => show(m, "error", d),
  }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div style={styles.region} role="status" aria-live="polite" aria-atomic="false">
        {toasts.map((t) => (
          <div key={t.id} style={styles.toast} onClick={() => dismiss(t.id)}>
            <Icon name={ICON[t.kind]} size={16} style={{ color: COLOR[t.kind], flexShrink: 0 }} />
            <span style={styles.msg}>{t.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const styles: Record<string, React.CSSProperties> = {
  region: {
    position: "fixed", left: 24, bottom: 24, zIndex: 2000,
    display: "flex", flexDirection: "column", gap: 8, pointerEvents: "none",
  },
  toast: {
    pointerEvents: "auto", cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
    background: "var(--bg-panel)", border: "1px solid var(--border)", borderRadius: "var(--radius)",
    boxShadow: "var(--shadow-1)", padding: "10px 14px", maxWidth: 360,
  },
  msg: { color: "var(--text)", fontSize: 13.5, fontWeight: 600 },
};
